import { Text, Tooltip } from "@mantine/core";
import { IconCopy, IconCheck } from "@tabler/icons-react";
import React, { useState } from "react";
import { useMediaQuery } from "@mantine/hooks";

interface CopyableFieldProps {
  value: string;
  maxWidth?: string;
  isSuggested?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  copyTexts?: {
    copy: string;
    copied: string;
  };
}

export const CopyableField: React.FC<CopyableFieldProps> = ({
  value,
  maxWidth = "25rem",
  isSuggested = false,
  onClick,
  disabled = false,
  copyTexts = {
    copy: "Copy",
    copied: "Copied",
  },
}) => {
  const isSmallScreen = useMediaQuery("(max-width: 588px)");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    console.log("handleCopy");
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onClick) {
        onClick();
      }
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    // Prevent event bubbling to avoid conflicts with Popover
    e.stopPropagation();
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    // Prevent event bubbling to avoid conflicts with Popover
    e.stopPropagation();
  };

  const fieldContent = (
    <div
      style={{
        border: "1px solid #CDCDCD",
        padding: "0.25rem 0.5rem",
        borderRadius: "0.25rem",
        backgroundColor: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        maxWidth: maxWidth,
        minWidth: "10rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.25rem",
        // Prevent text selection
        userSelect: "none",
        // Disable pointer events when disabled
        pointerEvents: disabled ? "none" : "auto",
      }}
      onClick={disabled ? undefined : handleCopy}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Text
        c="#1E1E1E"
        fw={600}
        style={{
          whiteSpace: isSmallScreen || isSuggested ? "normal" : "nowrap",
          overflow: isSmallScreen || isSuggested ? "visible" : "hidden",
          textOverflow: isSmallScreen || isSuggested ? "clip" : "ellipsis",
          flex: 1,
          cursor: "inherit",
          userSelect: "none",
        }}
      >
        {value}
      </Text>
      <div
        style={{
          cursor: "inherit",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {copied ? <IconCheck size={16} color="teal" /> : <IconCopy size={16} />}
      </div>
    </div>
  );

  return (
    <Tooltip
      zIndex={1000}
      label={copied ? copyTexts.copied : copyTexts.copy}
      withArrow
      position="right"
    >
      {fieldContent}
    </Tooltip>
  );
};
